import { Link } from 'react-router-dom';
import { Pencil, Plus } from 'lucide-react';
import { useGuides, useDomains } from '@/lib/queries';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Account-scoped guide gallery. Owner + admin can write; runners read.
 * Pencil overlays appear on guides owned by the active account
 * (which, post-redesign, is every visible guide — the predicate is
 * mainly defensive).
 */
export default function GuidesPage() {
  const { account, role } = useAuth();
  const guidesQ = useGuides();
  const domainsQ = useDomains();
  const canWrite = role === 'owner' || role === 'admin';

  if (guidesQ.isLoading || domainsQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (guidesQ.isError) {
    return <p className="text-sm text-destructive">{(guidesQ.error as Error).message}</p>;
  }
  // Defensive against shape drift — older service-worker-cached
  // bundles paired with a newer API can deliver a non-array body.
  // Treat anything non-iterable as empty rather than crashing.
  const guides = Array.isArray(guidesQ.data) ? guidesQ.data : [];
  const domainList = Array.isArray(domainsQ.data) ? domainsQ.data : [];
  const domainsById = new Map(domainList.map((d) => [d.id, d]));

  const grouped = new Map<string, typeof guides>();
  for (const g of guides) {
    const key = domainsById.get(g.domainId)?.slug ?? 'other';
    const list = grouped.get(key) ?? [];
    list.push(g);
    grouped.set(key, list);
  }

  return (
    <div className="space-y-10">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Library · Markdown guides
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            Guides
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Standard operating procedures for this property. Edit, add, and remove freely —
            anything you save here is yours, scoped to this account.
          </p>
        </div>
        {canWrite ? (
          <Link to="/guides/new" data-testid="new-guide">
            <Button className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden /> New guide
            </Button>
          </Link>
        ) : null}
      </header>

      {guides.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No guides yet. {canWrite ? 'Click "New guide" to write your first SOP.' : ''}
        </p>
      ) : null}

      {[...grouped.entries()].map(([domainSlug, list]) => (
        <section key={domainSlug} className="space-y-4">
          <div className="flex items-baseline justify-between border-b border-rule pb-2">
            <h2 className="font-display text-xl font-semibold tracking-tight">
              {domainsById.get(list[0]!.domainId)?.name ?? domainSlug}
            </h2>
            <Link
              to={`/domains/${domainSlug}`}
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              Domain →
            </Link>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {list.map((g) => {
              const showPencil = canWrite && g.accountId === account?.id;
              return (
                <div key={g.id} className="relative">
                  <Link
                    to={`/guides/${g.slug}`}
                    className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
                  >
                    <Card className="h-full transition-all border-rule group-hover:border-primary/30 group-hover:shadow-md group-hover:-translate-y-0.5">
                      <CardHeader className="space-y-1.5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {g.slug}
                        </p>
                        <CardTitle className="font-display text-lg leading-tight">
                          {g.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                        {g.lastReviewed ? `Reviewed ${g.lastReviewed}` : '—'}
                      </CardContent>
                    </Card>
                  </Link>
                  {showPencil ? (
                    <Link
                      to={`/guides/${g.slug}/edit`}
                      data-testid={`edit-${g.slug}`}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md border border-rule bg-background/90 text-muted-foreground hover:border-primary/40 hover:bg-accent/30 hover:text-foreground transition-colors"
                      aria-label={`Edit ${g.title}`}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
